import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import { OFFLINE_AVAILABLE_SHOP } from 'constants/shop-language'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { isOfflineAvailableShop } from 'lib/utils/shop-lang'
import getStockInfoFromStore from 'lib/api/stores'
import FindThisItemModal from './modal'
import styles from './styles.scss'
import { getAvailableStoreFromSizeOption, handleOpenStoreModal } from './utils'

const FindThisItemAtStoreCTA = ({
  product,
  selectedOption,
  setOptionMessage,
  label,
  t,
}) => {
  const [stockInfo, setStockInfo] = useState([])
  const [storeAvailable, setStoreAvailable] = useState([])
  const [storeAvailableFromSearch, setStoreAvailableFromSearch] = useState(null)
  const [shouldOpenStoreModal, setShouldOpenStoreModal] = useState(false)
  const isEnableFindItemFeature = Boolean(product?.id_product)

  useEffect(() => {
    let active = true
    const fetchStockInfoFromStore = async () => {
      try {
        const productStock = await getStockInfoFromStore(product.id_product)
        if (active && productStock?.stores) {
          setStockInfo(productStock.stores)
        }
      } catch (err) {
        if (active) setStockInfo([])
      }
    }

    setStockInfo([])
    if (isEnableFindItemFeature) {
      fetchStockInfoFromStore()
    }
    return () => {
      active = false
    }
  }, [isEnableFindItemFeature, product?.id_product])

  useEffect(() => {
    if (selectedOption && stockInfo) {
      getAvailableStoreFromSizeOption({
        selectedOption,
        setStoreAvailable,
        stockInfo,
      })
    }
  }, [selectedOption, stockInfo])

  if (!isEnableFindItemFeature || product?.is_coming_soon) {
    return null
  }

  return (
    <React.Fragment>
      <div className="find-pdp-item">
        <style jsx>{styles}</style>
        <Button
          type={BUTTON_TYPES.link}
          onClick={() =>
            handleOpenStoreModal({
              product,
              setShouldOpenStoreModal,
              setOptionMessage,
              stockInfo,
              selectedOption,
              t,
            })
          }
        >
          {label ||
            hasLocizeTranslation(
              t,
              'FIND_PDP_ITEM_AT_STORE_CTA',
              'Find Item At Store Near Me',
            )}
        </Button>
      </div>
      <FindThisItemModal
        productID={product.id_product}
        shouldOpenStoreModal={shouldOpenStoreModal}
        setShouldOpenStoreModal={setShouldOpenStoreModal}
        setStoreAvailableFromSearch={setStoreAvailableFromSearch}
        stockInfo={storeAvailableFromSearch || storeAvailable}
      />
    </React.Fragment>
  )
}

FindThisItemAtStoreCTA.defaultProps = {
  label: null,
  selectedOption: undefined,
}

FindThisItemAtStoreCTA.propTypes = {
  label: PropTypes.string,
  product: PropTypes.shape({
    id_product: PropTypes.number,
    is_coming_soon: PropTypes.bool,
    stock: PropTypes.shape({
      is_sold_out: PropTypes.bool,
    }),
  }).isRequired,
  selectedOption: PropTypes.shape({
    id_product_attribute: PropTypes.number,
  }),
  setOptionMessage: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

const MarketStoreCTA = ({ country, ...props }) => {
  // Reuse the configured offline-store capability. Singapore's store lookup
  // is explicitly unavailable even though its shared checkout flag is retained.
  const supportsStoreLookup =
    country !== 'sg' && isOfflineAvailableShop(OFFLINE_AVAILABLE_SHOP, country)

  if (
    !supportsStoreLookup ||
    !props.product?.id_product ||
    props.product?.is_coming_soon
  ) {
    return null
  }

  // A destination/product change must discard stock, search results and any
  // open modal before rendering the next scope. Late requests are ignored above.
  return (
    <FindThisItemAtStoreCTA
      key={`${country}:${props.product.id_product}`}
      {...props}
    />
  )
}

MarketStoreCTA.defaultProps = {
  ...FindThisItemAtStoreCTA.defaultProps,
  country: undefined,
}

MarketStoreCTA.propTypes = {
  ...FindThisItemAtStoreCTA.propTypes,
  country: PropTypes.string,
}

export default connect((state) => ({
  country: state.internationalization?.country,
}))(withI18next()(MarketStoreCTA))
